import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, Syringe, TrendingUp, Baby, Stethoscope, ShieldAlert, FileText, RefreshCw, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import axios from 'axios';
import { toast } from 'sonner';
import { useViewOnly } from '../contexts/ViewOnlyContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LandingPage = () => {
  const navigate = useNavigate();
  const { enableViewOnlyMode } = useViewOnly();
  const [showGuestDialog, setShowGuestDialog] = useState(false);
  const [guestId, setGuestId] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleGuestAccess = async () => {
    if (!guestId.trim()) {
      toast.error('Please enter an ID');
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/access/baby/${guestId.trim()}`);
      if (response.data) {
        // Store baby data in sessionStorage for guest access
        sessionStorage.setItem('viewOnlyBaby', JSON.stringify(response.data));
        
        // Close dialog
        setShowGuestDialog(false);
        setGuestId('');
        
        // Navigate to root with guest parameter - this triggers QR Access Handler in App.js
        window.location.href = '/?guest=' + guestId.trim();
      }
    } catch (error) {
      console.error('Guest access error:', error);
      if (error.response?.status === 404) {
        toast.error('ID not found. Please check and try again.');
      } else {
        toast.error('Unable to access. Please check the ID and try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const features = [
    {
      icon: Heart,
      title: 'Medical Condition',
      description: 'Track health conditions and vital signs.',
      gradient: 'from-red-500 to-red-600'
    },
    {
      icon: ShieldAlert,
      title: 'Allergies',
      description: 'Monitor allergies and allergen information.',
      gradient: 'from-blue-500 to-blue-800'
    },
    {
      icon: Syringe,
      title: 'Vaccinations',
      description: 'Never miss a vaccination with automatic reminders.',
      gradient: 'from-violet-500 to-violet-700'
    },
    {
      icon: TrendingUp,
      title: 'Growth Tracker',
      description: 'Track weight, height with visual growth charts.',
      gradient: 'from-pink-500 to-pink-600'
    },
    {
      icon: Baby,
      title: 'Birth Record',
      description: 'Store complete birth details and history.',
      gradient: 'from-amber-500 to-amber-600'
    },
    {
      icon: Stethoscope,
      title: 'Medical Sessions',
      description: 'Complete medical session records with prescriptions.',
      gradient: 'from-cyan-500 to-cyan-600'
    }
  ];

  const whyChooseUs = [
    {
      icon: FileText,
      title: 'Digital Prescriptions',
      description: 'Create and manage electronic prescriptions with ease.',
      gradient: 'from-indigo-500 to-indigo-600'
    },
    {
      icon: RefreshCw,
      title: 'Real-time Sync',
      description: 'Sync data seamlessly across all devices instantly.',
      gradient: 'from-emerald-500 to-emerald-600'
    },
    {
      icon: Clock,
      title: 'Save Time',
      description: 'Reduce administrative time by 60% efficiently.',
      gradient: 'from-teal-500 to-teal-600'
    },
    {
      icon: Heart,
      title: 'Improve Care',
      description: 'Boost patient satisfaction by 89% with better care.',
      gradient: 'from-sky-500 to-sky-600'
    }
  ];

  return (
    <div className="h-screen flex flex-col bg-white overflow-hidden">
      {/* Header/Navigation - Larger Size */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-shrink">
              <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 rounded-full flex items-center justify-center flex-shrink-0" style={{background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'}}>
                <Baby className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 text-white" />
              </div>
              <h1 className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-gray-900 truncate" style={{fontFamily: 'Space Grotesk'}}>
                My Health Record
              </h1>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-3 flex-shrink-0">
              <Button 
                variant="outline"
                onClick={() => navigate('/healthcare-login')}
                className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50 text-xs sm:text-sm md:text-base px-3 sm:px-4 py-1.5 sm:py-2 h-auto"
              >
                Healthcare
              </Button>
              <Button 
                onClick={() => setShowGuestDialog(true)}
                className="bg-blue-600 hover:bg-blue-700 text-xs sm:text-sm md:text-base px-3 sm:px-4 py-1.5 sm:py-2 h-auto whitespace-nowrap"
              >
                Guest Access
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content - No Scroll, Single Page - Mobile Optimized */}
      <div className="flex-1 flex items-center overflow-hidden">
        <div className="w-full">
          {/* App Branding - Centered */}
          <div className="flex justify-center items-center py-3 sm:py-4">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 rounded-full flex items-center justify-center" style={{background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'}}>
                <Baby className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 text-white" />
              </div>
              <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900" style={{fontFamily: 'Space Grotesk'}}>
                My Health Record
              </h1>
            </div>
          </div>

          {/* Features Section */}
          <section className="py-1.5 sm:py-2">
            <div className="w-full max-w-7xl mx-auto px-2 sm:px-3 lg:px-6">
              <div className="text-left mb-1.5 sm:mb-2">
                <h2 className="text-base sm:text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 mb-0.5 sm:mb-1" style={{fontFamily: 'Space Grotesk'}}>
                  Features
                </h2>
                <p className="text-[10px] sm:text-sm md:text-base lg:text-lg text-gray-600 hidden sm:block">
                  Everything you need to manage your health
                </p>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-1.5 sm:gap-2 md:gap-3 mb-2 sm:mb-3">
                {features.map((feature, index) => (
                  <div 
                    key={index}
                    className="group bg-white rounded-lg sm:rounded-xl p-1.5 sm:p-2 md:p-3 border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all duration-300"
                  >
                    <div className={`inline-flex p-1 sm:p-1.5 md:p-2 rounded-full bg-gradient-to-br ${feature.gradient} mb-1 sm:mb-1.5 md:mb-2 group-hover:scale-110 transition-transform`}>
                      <feature.icon className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 lg:w-6 lg:h-6 text-white" />
                    </div>
                    <h3 className="text-[10px] sm:text-xs md:text-sm lg:text-base font-bold text-gray-900 mb-0.5 sm:mb-1" style={{fontFamily: 'Space Grotesk'}}>
                      {feature.title}
                    </h3>
                    <p className="text-[8px] sm:text-[10px] md:text-xs lg:text-sm text-gray-600 leading-tight sm:leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Why Choose Us Section */}
          <section className="py-1.5 sm:py-2">
            <div className="w-full max-w-7xl mx-auto px-2 sm:px-3 lg:px-6">
              <div className="text-left mb-1.5 sm:mb-2">
                <h2 className="text-base sm:text-xl md:text-2xl lg:text-3xl font-bold text-gray-900" style={{fontFamily: 'Space Grotesk'}}>
                  Why Choose Us?
                </h2>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-1.5 sm:gap-2 md:gap-3">
                {whyChooseUs.map((item, index) => (
                  <div 
                    key={index}
                    className="group bg-white rounded-lg sm:rounded-xl p-1.5 sm:p-2 md:p-3 border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all duration-300"
                  >
                    <div className={`inline-flex p-1 sm:p-1.5 md:p-2 rounded-full bg-gradient-to-br ${item.gradient} mb-1 sm:mb-1.5 md:mb-2 group-hover:scale-110 transition-transform`}>
                      <item.icon className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 lg:w-6 lg:h-6 text-white" />
                    </div>
                    <h3 className="text-[10px] sm:text-xs md:text-sm lg:text-base font-bold text-gray-900 mb-0.5 sm:mb-1" style={{fontFamily: 'Space Grotesk'}}>
                      {item.title}
                    </h3>
                    <p className="text-[8px] sm:text-[10px] md:text-xs lg:text-sm text-gray-600 leading-tight sm:leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* Footer - Compact */}
      <footer className="bg-gray-50 border-t border-gray-200 py-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-[10px] sm:text-xs text-gray-600">&copy; 2025 My Health Record. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Guest Access Dialog */}
      <Dialog open={showGuestDialog} onOpenChange={setShowGuestDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{fontFamily: 'Space Grotesk'}}>Guest Access</DialogTitle>
            <DialogDescription>
              Enter the 8-digit ID to access health records
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Input
                placeholder="Enter ID (e.g., 12345678)"
                value={guestId}
                onChange={(e) => setGuestId(e.target.value.toUpperCase())}
                onKeyPress={(e) => e.key === 'Enter' && handleGuestAccess()}
                maxLength={36}
                className="text-center text-lg tracking-wider"
                disabled={isLoading}
              />
            </div>
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowGuestDialog(false);
                  setGuestId('');
                }}
                className="flex-1"
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleGuestAccess}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                disabled={isLoading}
              >
                {isLoading ? 'Accessing...' : 'Access'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LandingPage;
